/**
 * /auth/verify — fallback UI when the browser lands on the verify URL.
 * In practice the Worker 302-redirects on success; this page is only seen
 * if a user opens the verify URL in a context where the redirect cannot
 * complete (e.g., copy-pasted into a JS-disabled tab — very rare).
 *
 * Hits `GET /api/auth/verify?token=…` itself, then routes per result.
 */
import * as React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Skeleton } from '../../components/ui/Skeleton';

export default function Verify() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const returnTo = params.get('return_to') ?? '/';

  const [state, setState] = React.useState<
    'pending' | 'expired' | 'used' | 'invalid' | 'ok'
  >('pending');

  React.useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    // The verify endpoint is a 302 — the browser follows it and lands on
    // the redirected page. If our fetch resolves with a JSON envelope, it's
    // an error. We use credentials:include so the eventual cookie sticks.
    fetch(
      `/api/auth/verify?token=${encodeURIComponent(token)}&return_to=${encodeURIComponent(returnTo)}`,
      { credentials: 'include', redirect: 'follow' },
    )
      .then(async (res) => {
        if (res.ok || res.redirected) {
          setState('ok');
          // The browser already redirected. Replace the location.
          window.location.replace(returnTo);
          return;
        }
        if (res.status === 410) setState('expired');
        else if (res.status === 409) setState('used');
        else setState('invalid');
      })
      .catch(() => setState('invalid'));
  }, [token, returnTo]);

  if (state === 'pending' || state === 'ok') {
    return (
      <div className="mx-auto max-w-md w-full px-6 pt-20">
        <p className="text-center font-serif italic text-ink-700">
          Just a moment…
        </p>
        <div className="mt-8 space-y-3">
          <Skeleton className="h-4 w-3/4 mx-auto" />
          <Skeleton className="h-4 w-1/2 mx-auto" />
        </div>
      </div>
    );
  }
  if (state === 'expired') {
    return <Navigate to="/auth/request?expired=1" replace />;
  }
  if (state === 'used') {
    return <Navigate to="/auth/request?reused=1" replace />;
  }
  return <Navigate to="/auth/request" replace />;
}
